import {
  Stack,
  Text,
  Icon,
  Badge,
  Link,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelSection,
  InlineStack,
  StatusIndicator,
  Box,
} from '@customerio/pluma-components/react';
import { SEVERITY_COLOR, useEnvHealth, useWorkspaceHealthSimulation } from '@/features/workspace-health';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { generatePath } from '@/services/router/generate-path';
import { collapseProcessingAlerts } from '../utils';
import * as styles from './alerts-panel.css';

interface AlertsPanelProps {
  /** When true, return null instead of showing the empty state */
  hideWhenEmpty?: boolean;
}

export function AlertsPanel({ hideWhenEmpty = false }: AlertsPanelProps) {
  const workspaceId = getWorkspaceIdFromCurrentUrl() ?? 0;
  const { simulate } = useWorkspaceHealthSimulation();
  const healthQuery = useEnvHealth(workspaceId, simulate);
  const data = healthQuery.data;
  const alerts = data != null ? collapseProcessingAlerts(data.alerts) : [];
  const hasAlerts = alerts.length > 0;

  if (!hasAlerts && hideWhenEmpty) {
    return null;
  }

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>
          <InlineStack gap="050" alignBlock="center">
            Active issues
            {hasAlerts ? <Badge isEmphasized={true}>{data?.alerts.length}</Badge> : null}
          </InlineStack>
        </PanelTitle>
      </PanelHeader>
      <PanelSection withPadding={false}>
        {hasAlerts ? (
          <Stack px="300">
            {alerts.slice(0, 5).map((alert, index) => (
              <Stack
                key={index}
                gap="050"
                pt={index == 0 ? undefined : '200'}
                pb={index == alerts.slice(0, 5).length - 1 ? '300' : '200'}
                className={styles.categoryGroup}
              >
                <InlineStack gap="100" alignBlock="center">
                  <StatusIndicator color={SEVERITY_COLOR[alert.severity]} />
                  <Text size="s" weight="semibold">
                    {alert.title}
                  </Text>
                </InlineStack>
                <Text size="xs" color="subtle">
                  {alert.message}
                </Text>
                {alert.action_type === 'failed_events' ? (
                  <Box>
                    <Link
                      href={generatePath(
                        '/workspaces/:workspaceId/health',
                        { workspaceId },
                        { drawer: 'data-alerts', tab: 'data' },
                      )}
                      variant="secondary"
                    >
                      {alert.action_label}
                    </Link>
                  </Box>
                ) : null}
              </Stack>
            ))}
          </Stack>
        ) : (
          <InlineStack gap="100" alignBlock="center" px="300" pb="300">
            <Icon name="check-circle" size="sm" color="success" />
            <Text size="s" color="subtle">
              All clear — nothing needs your attention
            </Text>
          </InlineStack>
        )}
      </PanelSection>
    </Panel>
  );
}
